import React, { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    activeChartsSelector,
    getAnalyticsChartData,
    getAnalyticsStats,
    getDomainLinkingStatus,
    selectAppLoaded,
    selectHasActiveSubscription,
    selectHasBasicInfo,
    selectHasSeenConfetti,
    selectHydratedQuestsSorted,
    selectIsLoggedInToADomain,
    selectIsUserVerified,
    selectLoggedInDomains,
    selectTimeRange,
    selectUserInfo,
    TimeRange,
    useAppDispatch,
    useAppSelector
} from '@foxtail-dev/user-clients'
import { Box, useMediaQuery } from '@mui/material'
import { FoxTypography } from '../../components/common/FoxTypography'
import { getGreeting } from '../../utils/getGreeting'
import { ChartMarketButtons } from '../../components/home/ChartMarketButtons'
import { ReportsOverviewHeader } from '../../components/home/ReportsOverViewHeader'
import { LinkedMarketCards } from '../../components/home/LinkedMarketCards'
import { AllCharts } from '../../components/home/AllCharts'
import { AnalyticsBoxes } from '../../components/home/AnalyticsBoxes'
import { FlexGrow } from '../../components/common/FlexGrow'
import { EditChartsMenu } from '../../components/home/EditChartsMenu'
import { LoadingScreen } from '../LoadingScreen'
import { ChecklistBanner } from '../../containers/home/ChecklistBanner'
import { FoxButton } from '../../components/common/FoxButton'

const HomeScreen: React.FC = () => {
    const navigate = useNavigate()

    const appLoaded = useAppSelector(selectAppLoaded)
    const isUserVerified = useAppSelector(selectIsUserVerified)
    const hasBasicInfo = useAppSelector(selectHasBasicInfo)
    const userInfo = useAppSelector(selectUserInfo)
    const dispatch = useAppDispatch()

    const isLoggedIntoAMarket = useAppSelector(selectIsLoggedInToADomain)
    const loggedInDomains = useAppSelector(selectLoggedInDomains)
    const activeCharts = useAppSelector(activeChartsSelector)

    const hasSeenConfetti = useAppSelector(selectHasSeenConfetti)
    const currentTimeRangeRef = useRef<TimeRange>('week')
    currentTimeRangeRef.current = useAppSelector(selectTimeRange)
    const activeMarkets = useAppSelector(selectLoggedInDomains)
    const isMobile = useMediaQuery('(max-width:600px)')
    const [hasFinishedRedirecting, setHasFinishedRedirecting] = React.useState(false)
    const quests = useAppSelector(selectHydratedQuestsSorted)
    const hasActiveSubscription = useAppSelector(selectHasActiveSubscription)

    const areAllQuestsCompleted = useMemo(() => {
        return quests.every(quest => quest.quest.details.status === 'completed')
    }, [quests])

    useEffect(() => {
        dispatch(getAnalyticsChartData(currentTimeRangeRef.current))
        dispatch(getAnalyticsStats())

        const domainLinkingAndAnalyticsTotalsStatusInterval = setInterval(() => {
            dispatch(getDomainLinkingStatus())
        }, 5000)

        const chartStatsInterval = setInterval(() => {
            dispatch(getAnalyticsChartData(currentTimeRangeRef.current))
            dispatch(getAnalyticsStats())
        }, 30000)

        return () => {
            clearInterval(domainLinkingAndAnalyticsTotalsStatusInterval)
            clearInterval(chartStatsInterval)
        }
    }, [])

    useEffect(() => {
        if (appLoaded) {
            if (isUserVerified === false) {
                navigate('/enter-phone-number')
            } else if (!hasBasicInfo) {
                navigate('/basic-info')
            } else if (hasSeenConfetti === false) {
                navigate('/sign-up-complete')
            } else if (isMobile) {
                navigate('/mobile')
            } else if (!hasActiveSubscription) {
                navigate('/inactive-subscription')
            }
            setHasFinishedRedirecting(true)
        }
    }, [appLoaded, isMobile])

    // TODO: Sometimes seeing a brief flash of the home screen before redirecting to enter phone number, investigate and see why
    // In the mean time, show a loading screen until we navigate to the correct page
    if (!hasFinishedRedirecting) {
        return <LoadingScreen />
    }

    return (
        <Box sx={styles.container}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <FoxTypography sx={{ marginBottom: '35px', marginTop: '15px' }} variant='h5'>
                    {getGreeting()}, {userInfo?.firstName}!
                </FoxTypography>
                <FlexGrow />
                <FoxButton
                    primary
                    variant='contained'
                    text={isLoggedIntoAMarket ? '+ New Listing' : '+ Connect Markets'}
                    onFoxClick={{
                        kind: 'internal',
                        to: isLoggedIntoAMarket ? '/app/create-listing/new' : '/app/account/marketplace-connections'
                    }}
                />
            </Box>

            {!areAllQuestsCompleted && <ChecklistBanner imageSize='large' />}

            <AnalyticsBoxes />

            <ReportsOverviewHeader currentTimeRangeRef={currentTimeRangeRef} />

            <Box sx={{ display: 'flex', marginTop: '32px', height: '48px', alignItems: 'center' }}>
                <ChartMarketButtons activeMarkets={activeMarkets} />
                <FlexGrow />
                <EditChartsMenu />
            </Box>

            <AllCharts activeCharts={activeCharts} />

            <LinkedMarketCards loggedInDomains={loggedInDomains} />
        </Box>
    )
}

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    }
}

export default HomeScreen
